.root {
  display: contents;
}

.nowLine {
  position: absolute;
  z-index: 2;
  inset-block: 0;
  left: 0;
  pointer-events: none;
  will-change: transform;
}

.nowControls {
  position: absolute;
  z-index: 6;
  top: var(--space-2-5);
  right: var(--space-2-5);
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.timelineButton {
  --icon-action-outline-offset: 2px;
  width: 1.75rem;
  height: 1.75rem;
  border: 2px solid var(--glass-border);
  border-radius: var(--radius-md);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.12), rgba(124, 196, 255, 0.03)), var(--glass-fill);
  -webkit-backdrop-filter: var(--glass-blur);
  backdrop-filter: var(--glass-blur);
  color: #cbd5e1;
  box-shadow:
    0 0.5rem 1.5rem rgba(0, 0, 0, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.timelineButton svg {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: currentColor;
}

.timelineButton:hover {
  border-color: rgba(226, 232, 240, 0.9);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.18), rgba(124, 196, 255, 0.07)),
    rgba(12, 20, 34, 0.54);
  color: var(--color-fg-strong);
}

.hoverLine {
  position: absolute;
  z-index: 3;
  inset-block: 0;
  left: 0;
  width: 1px;
  pointer-events: none;
  background: rgba(226, 232, 240, 0.5);
  box-shadow: 0 0 0 1px rgba(5, 7, 13, 0.2);
  will-change: transform;
}

.rowHeader {
  position: absolute;
  z-index: 5;
  top: 0;
  left: 0;
  display: flex;
  width: fit-content;
  max-width: calc(100% - var(--timeline-row-inline-margin));
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-1) var(--space-1) var(--space-2);
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: var(--radius-md);
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.1), rgba(124, 196, 255, 0.025)),
    rgba(8, 14, 24, 0.38);
  -webkit-backdrop-filter: blur(0.75rem) saturate(135%);
  backdrop-filter: blur(0.75rem) saturate(135%);
  box-shadow:
    0 0.5rem 1.375rem rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  color: var(--color-fg-strong);
  font-family: var(--font-data);
  font-size: var(--text-xs);
  pointer-events: none;
  will-change: transform;
}

.rowHeader:hover,
.rowHeader:focus-within {
  z-index: 8;
}

.rowLabel {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: baseline;
  white-space: nowrap;
  text-wrap: nowrap;
}

.rowSymbol {
  flex: 0 0 auto;
  color: var(--color-fg-strong);
  font-weight: 600;
}

.rowSeparator {
  flex: 0 0 auto;
  margin-inline: 0.35em;
  color: var(--color-muted);
}

.rowSource {
  overflow: hidden;
  min-width: 0;
  flex: 1 1 auto;
  color: var(--color-fg);
  text-overflow: ellipsis;
}

.rowHeader[data-collapsing] {
  border-color: rgb(255 107 107 / var(--timeline-row-collapse-border-alpha, 0));
}

.removeRow {
  flex: 0 0 1.125rem;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: var(--radius-xs);
  color: var(--color-fg);
  pointer-events: auto;
  transition: color var(--duration-fast) ease;
}

.rowHeader[data-collapsing] .removeRow {
  color: color-mix(
    in srgb,
    var(--color-fg),
    rgb(255 107 107) calc(var(--timeline-row-collapse-border-alpha, 0) * 100%)
  );
}

.removeRow:hover {
  background: var(--color-error-soft);
  color: var(--color-error);
}

.removeRow svg {
  width: 0.875rem;
  height: 0.875rem;
}

.timeHover {
  position: absolute;
  z-index: 6;
  top: 0;
  left: 0;
  padding: 3px 6px;
  border: 2px solid var(--glass-border);
  border-radius: 7px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.12), rgba(124, 196, 255, 0.025)),
    var(--glass-fill);
  -webkit-backdrop-filter: var(--glass-blur);
  backdrop-filter: var(--glass-blur);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  color: var(--color-fg-strong);
  font: 11px var(--font-data);
  line-height: 15px;
  white-space: nowrap;
  pointer-events: none;
  will-change: transform;
}

.signalTooltip {
  position: absolute;
  z-index: 7;
  top: 0;
  left: 0;
  display: grid;
  place-items: center;
  padding-inline: var(--timeline-signal-tooltip-padding-x);
  border: var(--timeline-signal-tooltip-border-width) solid var(--glass-border);
  border-radius: 8px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.14), rgba(124, 196, 255, 0.035)),
    var(--glass-fill);
  -webkit-backdrop-filter: var(--glass-blur);
  backdrop-filter: var(--glass-blur);
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
  color: var(--color-fg-strong);
  font: var(--timeline-signal-tooltip-font);
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
  will-change: transform;
}

@media (pointer: coarse) {
  .timelineButton {
    width: var(--control-height-touch);
    height: var(--control-height-touch);
  }

  .rowHeader {
    min-height: 2rem;
  }

  .removeRow {
    width: 1.75rem;
    height: 1.75rem;
    flex-basis: 1.75rem;
  }
}
